import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getAccrualExplanation,
  recalculateBalance,
  type AccrualExplanation,
} from '@/lib/absences';
import { formatDate, formatDateTime, toDateKey } from '@/lib/date';
import { usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Calendar,
  CalendarClock,
  ChevronDown,
  Download,
  Info,
  Loader2,
  RefreshCw,
  Sparkles,
  X as XMark,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface AccrualExplanationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  absenceTypeId: string;
  absenceTypeName: string;
  absenceTypeColor: string;
}

function sameDay(a: string, b: string): boolean {
  return toDateKey(a) === toDateKey(b);
}

// "Earns 10 days every 6 months" — natural-language interval phrase.
function intervalPhrase(interval: string, count: number): string {
  switch (interval) {
    case 'day':
      return count === 1 ? 'every day' : `every ${count} days`;
    case 'week':
      return count === 1 ? 'every week' : `every ${count} weeks`;
    case 'month':
      return count === 1 ? 'every month' : `every ${count} months`;
    case 'quarter':
      return count === 1 ? 'every quarter' : `every ${count} quarters`;
    case 'half_year':
      return count === 1 ? 'every 6 months' : `every ${count * 6} months`;
    case 'year':
      return count === 1 ? 'every year' : `every ${count} years`;
    default:
      return `every ${count} ${interval}`;
  }
}

function yearTypePhrase(yearType: string): string {
  return yearType === 'individual'
    ? 'anchored to the hire anniversary'
    : 'aligned to calendar boundaries';
}

function timingPhrase(timing: string): string {
  return timing === 'start'
    ? 'credited at the start of each cycle'
    : 'credited at the end of each cycle';
}

function startTriggerPhrase(startAccrual: string, startDelayDays: number): string {
  switch (startAccrual) {
    case 'after_probation':
      return 'Accrual begins after the probation period ends.';
    case 'after_delay':
      return `Accrual begins ${startDelayDays} day${startDelayDays === 1 ? '' : 's'} after the hire date.`;
    case 'immediately':
    default:
      return 'Accrual begins immediately from the hire date.';
  }
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-2">
      {children}
    </h4>
  );
}

// Single row in the breakdown — left label/sublabel, right value, optional sign.
function FormulaRow({
  label,
  sublabel,
  value,
  sign,
  highlight,
}: {
  label: string;
  sublabel?: string;
  value: string;
  sign?: '+' | '−' | '=';
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 py-2',
        highlight && 'border-t border-border/60 pt-2.5 mt-1'
      )}
    >
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'text-[13px] leading-tight',
            highlight ? 'font-semibold text-foreground' : 'text-foreground/90'
          )}
        >
          {label}
        </div>
        {sublabel && (
          <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{sublabel}</div>
        )}
      </div>
      <div className="flex items-baseline gap-1.5 shrink-0 tabular-nums">
        {sign && (
          <span
            className={cn(
              'text-[13px] w-3 text-right',
              highlight ? 'text-foreground font-semibold' : 'text-muted-foreground'
            )}
          >
            {sign}
          </span>
        )}
        <span
          className={cn(
            'text-[13px]',
            highlight ? 'text-primary font-semibold text-[15px]' : 'font-medium text-foreground'
          )}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

function TimelineRow({
  icon,
  label,
  date,
  detail,
}: {
  icon: React.ReactNode;
  label: string;
  date: string;
  detail?: string;
}) {
  return (
    <div className="flex items-start gap-3 py-1.5">
      <div className="shrink-0 mt-0.5 text-muted-foreground/80">{icon}</div>
      <div className="flex-1 min-w-0 flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] text-foreground">{label}</div>
          {detail && (
            <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{detail}</div>
          )}
        </div>
        <div className="text-[13px] text-muted-foreground tabular-nums shrink-0">{date}</div>
      </div>
    </div>
  );
}

function ExplanationSkeleton() {
  return (
    <div className="space-y-5 py-1">
      <Skeleton className="h-12 w-full rounded-md" />
      <div className="space-y-1.5">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-7 w-full rounded-md" />
        ))}
      </div>
      <Skeleton className="h-32 w-full rounded-md" />
      <Skeleton className="h-24 w-full rounded-md" />
    </div>
  );
}

function ExplanationContent({
  data,
  employeeId,
  absenceTypeId,
}: {
  data: AccrualExplanation;
  employeeId: string;
  absenceTypeId: string;
}) {
  const { config, employee, calculation, currentBalance, schedule, nextAccrual } = data;
  const [showTechnical, setShowTechnical] = useState(false);
  // The stored-vs-calculated drift notice and its Recalculate action are an
  // absence-configuration concern — only surface them to users who can manage
  // absence types (the recalculate route is itself restricted to HR admins).
  const canManageAbsences = usePermission('absences.types.manage');

  const queryClient = useQueryClient();
  const recalcMutation = useMutation({
    mutationFn: () => recalculateBalance(employeeId, absenceTypeId, currentBalance?.year),
    onSuccess: () => {
      toast.success('Balance recalculated');
      // Refresh every view of this balance immediately (no page reload):
      //  - this breakdown dialog (['accrual-explanation', ...])
      //  - the Absences-tab balance chips (['employee-policies', employeeId]
      //    and the use-employee-policies hook keys under ['employee-policies'])
      //  - the request dialog's assigned-types/balance list
      queryClient.invalidateQueries({ queryKey: ['accrual-explanation', employeeId, absenceTypeId] });
      queryClient.invalidateQueries({ queryKey: ['employee-policies'] });
      queryClient.invalidateQueries({ queryKey: ['employee-assigned-absence-types', employeeId] });
    },
    onError: (err: Error) => {
      toast.error('Failed to recalculate', { description: err.message });
    },
  });

  if (!config.accrualEnabled) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertTriangle className="h-8 w-8 text-amber-500/60" />
        <p className="mt-3 text-sm font-medium">Accrual is disabled</p>
        <p className="mt-1 text-xs text-muted-foreground">
          This absence type does not have automatic accrual configured.
        </p>
      </div>
    );
  }

  const balanceDrift =
    currentBalance != null
      ? Math.round((calculation.totalAccrued - currentBalance.accruedDays) * 100) / 100
      : null;

  const hasImportSnapshot = currentBalance?.importedAt != null;
  const importDateFormatted = currentBalance?.importedAt
    ? formatDateTime(currentBalance.importedAt, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const hireDate = employee.startDate;
  const scheduleStarts = calculation.accrualStartDate;
  const showSeparateScheduleStart = !sameDay(hireDate, scheduleStarts);

  // Phrase for "accrued since X" depending on snapshot presence
  const accruedSinceLabel = hasImportSnapshot
    ? `Accrued since import (${importDateFormatted})`
    : `Accrued since ${formatDate(scheduleStarts)}`;
  const cyclesSentence = (() => {
    const n = calculation.periodsElapsed;
    if (n === 0) return 'No cycles completed yet';
    if (n === 1) return '1 cycle completed';
    return `${n} cycles completed`;
  })();

  return (
    <div className="space-y-6">
      {/* SECTION 1: How accrual works (natural language) */}
      <section>
        <SectionTitle>Accrual rule</SectionTitle>
        <div className="rounded-md border bg-muted/30 px-3 py-2.5 space-y-1.5">
          <p className="text-[13px] leading-snug text-foreground">
            Earns <span className="font-semibold">{config.accrualAmount} days</span>{' '}
            <span className="font-medium">
              {intervalPhrase(config.accrualInterval, config.accrualIntervalCount)}
            </span>
            , {yearTypePhrase(config.accrualYearType)}.
          </p>
          <p className="text-[12px] text-muted-foreground leading-snug">
            {timingPhrase(config.accrualTiming)}.{' '}
            {startTriggerPhrase(config.startAccrual, config.startDelayDays)}
          </p>

          <button
            type="button"
            onClick={() => setShowTechnical((s) => !s)}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors mt-1 cursor-pointer"
          >
            <ChevronDown
              className={cn('h-3 w-3 transition-transform', showTechnical && 'rotate-180')}
            />
            {showTechnical ? 'Hide' : 'Show'} technical config
          </button>

          {showTechnical && (
            <div className="flex flex-wrap gap-1.5 pt-1.5">
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 font-mono">
                interval: {config.accrualInterval}
              </Badge>
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 font-mono">
                intervalCount: {config.accrualIntervalCount}
              </Badge>
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 font-mono">
                amount: {config.accrualAmount}
              </Badge>
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 font-mono">
                timing: {config.accrualTiming}
              </Badge>
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 font-mono">
                yearType: {config.accrualYearType}
              </Badge>
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 font-mono">
                startAccrual: {config.startAccrual}
              </Badge>
              {config.startDelayDays > 0 && (
                <Badge variant="secondary" className="text-[10px] py-0 px-1.5 font-mono">
                  startDelayDays: {config.startDelayDays}
                </Badge>
              )}
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 font-mono">
                firstAccrualCalculation: {config.firstAccrualCalculation}
              </Badge>
            </div>
          )}
        </div>
      </section>

      {/* SECTION 2: Timeline */}
      <section>
        <SectionTitle>Timeline</SectionTitle>
        <div className="rounded-md border px-3 py-1 divide-y divide-border/60">
          <TimelineRow
            icon={<Calendar className="h-3.5 w-3.5" />}
            label="Hired"
            date={formatDate(hireDate)}
            detail={
              !showSeparateScheduleStart
                ? 'Accrual schedule starts on the same day'
                : undefined
            }
          />
          {showSeparateScheduleStart && (
            <TimelineRow
              icon={<CalendarClock className="h-3.5 w-3.5" />}
              label="Accrual schedule starts"
              date={formatDate(scheduleStarts)}
              detail={calculation.accrualStartReason.split(' · ')[0]}
            />
          )}
          {hasImportSnapshot && importDateFormatted && (
            <TimelineRow
              icon={<Download className="h-3.5 w-3.5" />}
              label="Imported from PeopleForce"
              date={importDateFormatted}
              detail={`PF reported ${(currentBalance?.importedFromPfDays ?? 0).toFixed(1)} days available; accrual continues forward from the import date`}
            />
          )}
        </div>
      </section>

      {/* SECTION 3+4: Breakdown told in user-facing terms — no gross-cumulative
          intermediate. For migrated employees: the PeopleForce snapshot is shown
          as its NET value (what PF actually reported), accruals are post-import
          only, deductions are post-import activity only. Pre-import usage is
          baked into the snapshot value and never re-surfaces in the formula. */}
      {currentBalance && (() => {
        // Derive post-import activity from stored balance + import metadata.
        // - reservedAtImport = how much was already used or pending at import
        //   (gross - net = the difference between calculator's snapshot and PF's
        //    reported net balance)
        // - localActivitySinceImport = (used + pending now) − reservedAtImport.
        //   ≥ 0 in normal operation; clamped to 0 if a manual adjustment has
        //   reduced usedDays/pendingDays below the import-time level.
        const reservedAtImport = hasImportSnapshot
          ? Math.max(0, calculation.initialBalance - (currentBalance.importedFromPfDays ?? 0))
          : 0;
        const localActivitySinceImport = hasImportSnapshot
          ? Math.max(
              0,
              currentBalance.usedDays + currentBalance.pendingDays - reservedAtImport
            )
          : 0;
        return (
          <section>
            <SectionTitle>How we get to today&apos;s balance</SectionTitle>
            <div className="rounded-md border px-3.5 py-1">
              {hasImportSnapshot ? (
                <>
                  <FormulaRow
                    label="Imported from PeopleForce"
                    sublabel={`As of ${importDateFormatted}${
                      reservedAtImport > 0
                        ? ` · already reflects ${reservedAtImport.toFixed(1)} day${reservedAtImport === 1 ? '' : 's'} taken before this date`
                        : ''
                    }`}
                    value={(currentBalance.importedFromPfDays ?? 0).toFixed(1)}
                    sign="+"
                  />
                  <FormulaRow
                    label={`Accrued since import (${importDateFormatted})`}
                    sublabel={`${cyclesSentence} · ${config.accrualAmount} days per cycle`}
                    value={calculation.baseAmount.toFixed(1)}
                    sign="+"
                  />
                  {localActivitySinceImport > 0 && (
                    <FormulaRow
                      label="Used or reserved since import"
                      sublabel="New absences taken or pending requests since the migration"
                      value={localActivitySinceImport.toFixed(1)}
                      sign="−"
                    />
                  )}
                </>
              ) : (
                <>
                  {calculation.initialBalance > 0 && (
                    <FormulaRow
                      label="Joining balance"
                      sublabel="One-time, granted on policy assignment"
                      value={calculation.initialBalance.toFixed(1)}
                      sign="+"
                    />
                  )}
                  <FormulaRow
                    label={accruedSinceLabel}
                    sublabel={`${cyclesSentence} · ${config.accrualAmount} days per cycle`}
                    value={calculation.baseAmount.toFixed(1)}
                    sign="+"
                  />
                  {calculation.isProrated && calculation.prorationFactor != null && (
                    <FormulaRow
                      label="First-year proration"
                      sublabel={`Factor ×${calculation.prorationFactor.toFixed(2)}`}
                      value={calculation.proratedAmount.toFixed(1)}
                      sign="="
                    />
                  )}
                  {calculation.tenureBonus > 0 && (
                    <FormulaRow
                      label="Tenure bonus"
                      sublabel={calculation.tenureBonusReason ?? undefined}
                      value={calculation.tenureBonus.toFixed(1)}
                      sign="+"
                    />
                  )}
                  {currentBalance.carriedOverDays > 0 && (
                    <FormulaRow
                      label="Carried over from previous year"
                      value={currentBalance.carriedOverDays.toFixed(1)}
                      sign="+"
                    />
                  )}
                  <FormulaRow
                    label="Used (already taken)"
                    value={currentBalance.usedDays.toFixed(1)}
                    sign="−"
                  />
                  <FormulaRowWithTooltip
                    label="Pending"
                    tooltip="Days requested but not yet approved (or approved but the absence hasn't started yet). Reserved against your balance to prevent over-booking."
                    value={currentBalance.pendingDays.toFixed(1)}
                    sign="−"
                  />
                </>
              )}

              {/* The single highlighted answer */}
              <FormulaRow
                label="Available today"
                value={`${currentBalance.available.toFixed(1)} days`}
                sign="="
                highlight
              />
            </div>

            {canManageAbsences && balanceDrift !== null && balanceDrift !== 0 && (
              <div
                className={cn(
                  'flex items-start gap-2.5 rounded-md border px-3 py-2.5 mt-2',
                  balanceDrift > 0
                    ? 'bg-amber-100/80 border-amber-300 dark:bg-amber-500/10 dark:border-amber-500/30'
                    : 'bg-blue-100/80 border-blue-300 dark:bg-blue-500/10 dark:border-blue-500/30'
                )}
              >
                <AlertTriangle
                  className={cn(
                    'h-4 w-4 shrink-0 mt-0.5',
                    balanceDrift > 0
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-blue-600 dark:text-blue-400'
                  )}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] leading-snug text-foreground">
                    Stored balance differs from the calculated value: stored{' '}
                    <span className="font-mono font-semibold">
                      {currentBalance.accruedDays.toFixed(1)}
                    </span>
                    , calculated{' '}
                    <span className="font-mono font-semibold">
                      {calculation.totalAccrued.toFixed(1)}
                    </span>{' '}
                    ({balanceDrift > 0 ? '+' : ''}
                    {balanceDrift.toFixed(1)}d).
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={recalcMutation.isPending}
                        className="h-7 mt-2 text-[11px] gap-1 cursor-pointer bg-background"
                      >
                        {recalcMutation.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                        Apply calculated value
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Apply calculated balance?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This reconciles the stored balance to the freshly calculated value.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="rounded-md border bg-muted/30 divide-y divide-border/60 text-foreground">
                        <div className="flex items-center justify-between px-3 py-2">
                          <span className="text-[12px] text-muted-foreground">
                            Accrued (total earned)
                          </span>
                          <span className="font-mono tabular-nums text-[13px]">
                            {currentBalance.accruedDays.toFixed(1)}
                            <span className="mx-1.5 text-muted-foreground">→</span>
                            <span className="font-semibold text-primary">
                              {calculation.totalAccrued.toFixed(1)}
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between px-3 py-2">
                          <span className="text-[12px] text-muted-foreground">Available (usable)</span>
                          <span className="font-mono tabular-nums text-[13px]">
                            {currentBalance.available.toFixed(1)}
                            <span className="mx-1.5 text-muted-foreground">→</span>
                            <span className="font-semibold text-primary">
                              {(
                                calculation.totalAccrued +
                                currentBalance.carriedOverDays -
                                currentBalance.usedDays -
                                currentBalance.pendingDays
                              ).toFixed(1)}
                            </span>
                          </span>
                        </div>
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="cursor-pointer"
                          onClick={() => recalcMutation.mutate()}
                        >
                          Apply
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            )}
          </section>
        );
      })()}

      {/* SECTION 5: Next accrual banner */}
      {nextAccrual && (
        <section>
          <SectionTitle>Next accrual</SectionTitle>
          <div className="flex items-center gap-3 rounded-md border border-emerald-500/20 bg-emerald-500/5 px-3 py-2.5">
            <Sparkles className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-foreground">
                <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                  +{nextAccrual.amount} days
                </span>{' '}
                on{' '}
                <span className="font-semibold text-foreground">
                  {formatDate(nextAccrual.date)}
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {config.accrualYearType === 'individual'
                  ? 'Aligned to the hire anniversary'
                  : 'Aligned to calendar boundaries'}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 6: Upcoming Schedule */}
      {schedule.length > 0 && (
        <section>
          <SectionTitle>Upcoming schedule</SectionTitle>
          <div className="rounded-md border overflow-hidden text-[12px]">
            <div className="grid grid-cols-[1fr_auto_auto] bg-muted/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
              <span>Date</span>
              <span className="w-16 text-right">Accrual</span>
              <span className="w-20 text-right">Cumulative</span>
            </div>
            {schedule.map((entry, i) => (
              <div
                key={i}
                className={cn(
                  'grid grid-cols-[1fr_auto_auto] px-3 py-1.5 tabular-nums',
                  i % 2 === 0 ? 'bg-transparent' : 'bg-muted/20'
                )}
              >
                <span className="text-foreground">{formatDate(entry.date)}</span>
                <span className="w-16 text-right font-medium text-foreground">
                  +{entry.amount}
                </span>
                <span className="w-20 text-right text-muted-foreground">
                  {entry.cumulativeTotal}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function FormulaRowWithTooltip({
  label,
  tooltip,
  value,
  sign,
}: {
  label: string;
  tooltip: string;
  value: string;
  sign?: '+' | '−' | '=';
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0 flex-1 flex items-center gap-1">
        <span className="text-[13px] leading-tight text-foreground/90">{label}</span>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-muted-foreground/60 hover:text-muted-foreground transition-colors">
                <Info className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[260px] text-[11px] leading-snug">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="flex items-baseline gap-1.5 shrink-0 tabular-nums">
        {sign && <span className="text-[13px] w-3 text-right text-muted-foreground">{sign}</span>}
        <span className="text-[13px] font-medium text-foreground">{value}</span>
      </div>
    </div>
  );
}

export function AccrualExplanationDialog({
  open,
  onOpenChange,
  employeeId,
  absenceTypeId,
  absenceTypeName,
  absenceTypeColor,
}: AccrualExplanationDialogProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['accrual-explanation', employeeId, absenceTypeId],
    queryFn: () => getAccrualExplanation(employeeId, absenceTypeId),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[500px] gap-0 p-0 overflow-x-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <div
              className="h-2.5 w-2.5 rounded-full shrink-0"
              style={{ backgroundColor: absenceTypeColor }}
            />
            Accrual breakdown
          </DialogTitle>
          <DialogDescription className="text-xs">
            How the {absenceTypeName.toLowerCase()} balance is computed for this employee
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(85vh-90px)]">
          <div className="px-5 py-5">
            {isLoading && <ExplanationSkeleton />}
            {error && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <XMark className="h-8 w-8 text-destructive/60" />
                <p className="mt-2 text-sm font-medium">Failed to load</p>
                <p className="mt-1 text-xs text-muted-foreground">{(error as Error).message}</p>
              </div>
            )}
            {data && (
              <ExplanationContent
                data={data}
                employeeId={employeeId}
                absenceTypeId={absenceTypeId}
              />
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
